import {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { FormInstance } from 'antd/lib/form/hooks/useForm'
import { VendorDetailsForm } from '../../MyCommunity/Vendors/VendorCreation/General/DetailsForm/types'
import { Form } from 'antd'
import {
  setVendorInfoError,
  updateVendorInfo
} from '../../../redux/store/vendorInfo/slice'
import { useSelector } from 'react-redux'
import { getUser } from '../../../redux/store/user/getters'
import {
  getError,
  getVendorInfo
} from '../../../redux/store/vendorInfo/getters'
import {
  convertDataToUpdateDetails,
  convertDetailsToForm
} from '../../MyCommunity/Vendors/VendorCreation/General/DetailsForm/utils'
import { IVendorInfoErrors } from '../../../redux/store/vendorInfo/types'
import { getObjValueByQuery } from '../../../helper/common'
import { useAppDispatch } from '../../../redux/createStore'

type ContextProps = {
  state: {
    form: FormInstance<VendorDetailsForm>
    phoneNumber?: string
    isActive: boolean
    hasFormChanges: boolean
    errors: IVendorInfoErrors | null
  }
  actions: {
    onSubmit: (data: VendorDetailsForm) => void
    handleChangeForm: () => void
    setIsActive: Dispatch<SetStateAction<boolean>>
    resetFormChanges: () => void
    getErrorField: (query: string[]) => string
  }
}

const VendorDetailsContext = createContext<ContextProps>({
  state: {} as ContextProps['state'],
  actions: {} as ContextProps['actions']
})

const VendorDetailsContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const user = useSelector(getUser)
  const dispatch = useAppDispatch()
  const fetchedVendorInfo = useSelector(getVendorInfo)
  const vendorErrors = useSelector(getError)
  const [hasFormChanges, _setHasFormChanges] = useState(false)
  const [isActive, setIsActive] = useState(false)
  const [errors, _setErrors] = useState<IVendorInfoErrors | null>(null)

  const getErrorField = useCallback(
    (query: string[]) =>
      (errors && (getObjValueByQuery(errors, query) as string)) ?? '',
    [errors]
  )

  const [form] = Form.useForm<VendorDetailsForm>()
  const phoneNumber = Form.useWatch(['vendor', 'phone_number'], form)

  const handleChangeForm = useCallback(() => {
    _setErrors({})
    if (!hasFormChanges) {
      _setHasFormChanges(true)
    }
  }, [hasFormChanges])

  const _resetFloatFooter = useCallback(() => {
    _setHasFormChanges(false)
    setIsActive(false)
    _setErrors(null)
  }, [])

  const resetFormChanges = useCallback(() => {
    form.setFieldsValue({
      ...convertDetailsToForm(fetchedVendorInfo)
    })
    _resetFloatFooter()
  }, [_resetFloatFooter, fetchedVendorInfo, form])

  useEffect(() => {
    resetFormChanges()
  }, [resetFormChanges])

  useEffect(() => {
    _setErrors(vendorErrors)
  }, [vendorErrors])

  useEffect(
    () => () => {
      dispatch(setVendorInfoError({ error: null }))
    },
    [dispatch]
  )

  const onSubmit = useCallback(
    (data: VendorDetailsForm) => {
      if (user?.vendor) {
        dispatch(
          updateVendorInfo(
            convertDataToUpdateDetails(data),
            user?.vendor,
            _resetFloatFooter
          )
        )
      }
    },
    [_resetFloatFooter, dispatch, user?.vendor]
  )

  const context = useMemo(
    () => ({
      state: {
        form,
        phoneNumber,
        isActive,
        hasFormChanges,
        errors
      },
      actions: {
        onSubmit,
        handleChangeForm,
        setIsActive,
        resetFormChanges,
        getErrorField
      }
    }),
    [
      form,
      phoneNumber,
      isActive,
      hasFormChanges,
      errors,
      onSubmit,
      handleChangeForm,
      resetFormChanges,
      getErrorField
    ]
  )

  return (
    <VendorDetailsContext.Provider value={context}>
      {children}
    </VendorDetailsContext.Provider>
  )
}

export const useVendorDetailsContext = () => useContext(VendorDetailsContext)

export default VendorDetailsContextProvider
